import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Database, Download, FileSpreadsheet, HardDrive, LayoutDashboard, List, Menu, RefreshCw, Search, Upload, X } from 'lucide-react';
import { Bar, BarChart, Pie, PieChart, Cell, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const BRAND = {
  primary: 'bg-[#74c476]',
  primaryHover: 'hover:bg-[#5aa85c]',
  secondary: 'bg-[#222222]',
  light: 'bg-stone-50'
};

const CHART_COLORS = ['#74c476', '#5aa85c', '#4E647D', '#E4B063', '#D76559', '#6b7280', '#22c55e'];

const authFetchLocal = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{label}</div>
    <div className="text-2xl font-bold text-stone-900">{value}</div>
    {hint ? <div className="text-sm text-stone-500 mt-1">{hint}</div> : null}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
    <h3 className="text-sm font-bold mb-3">{title}</h3>
    <div className="h-[300px]">{children}</div>
  </div>
);

export default function SpanishExecutiveBillsWorkspace({ user, onLogout, onBackToHub, apiUrl }) {
  const [view, setView] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ legislatures: [], years: [], typeLaws: [], results: [], capTopics: [], capSubtopics: [] });

  const [search, setSearch] = useState('');
  const [legislature, setLegislature] = useState('');
  const [year, setYear] = useState('');
  const [typeLaw, setTypeLaw] = useState('');
  const [result, setResult] = useState('');
  const [capTopic, setCapTopic] = useState('');
  const [capSubtopic, setCapSubtopic] = useState('');
  const [importMsg, setImportMsg] = useState(null);

  const canImport = user && user.role === 'admin';
  const displayUserName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.fullName || user?.username || 'User';

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-executive-bills/dashboard`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load dashboard');
      setDashboard(data);
    } catch (error) {
      console.error(error);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-executive-bills/filters`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load filters');
      setFilters({
        legislatures: Array.isArray(data?.legislatures) ? data.legislatures : [],
        years: Array.isArray(data?.years) ? data.years : [],
        typeLaws: Array.isArray(data?.typeLaws) ? data.typeLaws : [],
        results: Array.isArray(data?.results) ? data.results : [],
        capTopics: Array.isArray(data?.capTopics) ? data.capTopics : [],
        capSubtopics: Array.isArray(data?.capSubtopics) ? data.capSubtopics : []
      });
    } catch (error) {
      console.error(error);
      setFilters({ legislatures: [], years: [], typeLaws: [], results: [], capTopics: [], capSubtopics: [] });
    }
  }, [apiUrl]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (legislature) params.set('legislature', legislature);
      if (year) params.set('year', year);
      if (typeLaw) params.set('typeLaw', typeLaw);
      if (result) params.set('result', result);
      if (capTopic) params.set('capTopic', capTopic);
      if (capSubtopic) params.set('capSubtopic', capSubtopic);
      const query = params.toString();

      const res = await authFetchLocal(`${apiUrl}/spanish-executive-bills/bills${query ? `?${query}` : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load executive bills');
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, capSubtopic, capTopic, legislature, result, search, typeLaw, year]);

  useEffect(() => {
    fetchDashboard();
    fetchFilters();
  }, [fetchDashboard, fetchFilters]);

  useEffect(() => {
    if (view === 'bills') fetchRows();
  }, [fetchRows, view]);

  const downloadBlobFromEndpoint = async (endpoint, fallbackFilename) => {
    const res = await authFetchLocal(`${apiUrl}${endpoint}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || 'Export failed');
    }

    const contentDisposition = res.headers.get('content-disposition') || '';
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    let resolvedFilename = fallbackFilename;
    if (filenameMatch?.[1]) {
      try {
        resolvedFilename = decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
      } catch {
        resolvedFilename = filenameMatch[1].replace(/"/g, '').trim() || fallbackFilename;
      }
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = resolvedFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportMsg(null);
    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-executive-bills/import-xlsx`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Import failed');

      setImportMsg({
        type: 'success',
        text: `Imported rows: ${Number(data?.summary?.totalRows || 0)}`
      });

      await Promise.all([fetchDashboard(), fetchFilters()]);
      if (view === 'bills') await fetchRows();
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Import failed' });
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleExportCsv = async () => {
    try {
      await downloadBlobFromEndpoint('/spanish-executive-bills/export-csv', `spanish_executive_bills_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportXlsx = async () => {
    try {
      await downloadBlobFromEndpoint('/spanish-executive-bills/export-xlsx', `spanish_executive_bills_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportOriginal = async () => {
    try {
      await downloadBlobFromEndpoint('/spanish-executive-bills/export-original', `spanish_executive_bills_original_${new Date().toISOString().split('T')[0]}.sql`);
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const legislatureChartRows = useMemo(
    () => (dashboard?.byLegislature || []).slice(0, 15).map((item) => ({ label: item.label, count: Number(item.count || 0) })),
    [dashboard]
  );

  const typeLawChartRows = useMemo(
    () => (dashboard?.byTypeLaw || []).slice(0, 8).map((item) => ({ label: item.label, count: Number(item.count || 0) })),
    [dashboard]
  );

  const resultChartRows = useMemo(
    () => (dashboard?.byResult || []).slice(0, 8).map((item) => ({ label: item.label, count: Number(item.count || 0) })),
    [dashboard]
  );

  const capTopicChartRows = useMemo(
    () => (dashboard?.byCapTopic || []).slice(0, 10).map((item) => ({ label: item.label, count: Number(item.count || 0) })),
    [dashboard]
  );

  return (
    <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
      {loading && !dashboard && (
        <div className="fixed inset-0 z-[70] bg-stone-50 flex items-center justify-center" role="status" aria-live="polite">
          <div className="flex flex-col items-center text-center px-6">
            <RefreshCw className="animate-spin text-green-600 mb-4" size={38} />
            <p className="text-lg font-bold text-stone-800">Loading database…</p>
            <p className="mt-1 text-sm text-stone-500">Preparing visualizations and filters</p>
          </div>
        </div>
      )}
      <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
        <div className="flex flex-col">
          <h1 className="text-lg font-bold tracking-tight">Executive Bills</h1>
          <p className="text-xs text-stone-300">Data workspace</p>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-1">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      <aside className={`fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="p-6 border-b border-stone-700 hidden md:block">
          <h1 className="text-xl font-sans font-bold tracking-tight">Executive Bills</h1>
          <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Legislative Database</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <div className="px-4 py-2 mb-4">
            <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
            <div className="font-medium text-white truncate">{displayUserName}</div>
            <div className="flex justify-between items-center text-xs mt-1">
              <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user?.role}</span>
              <button onClick={onLogout} title="Log Out">
                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          <button onClick={() => { setView('dashboard'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'dashboard' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </button>

          <button onClick={() => { setView('bills'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'bills' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
            <List size={18} />
            <span>Executive Bills</span>
          </button>

          <div className="pt-4 mt-4 border-t border-stone-700 space-y-1">
            {user?.role !== 'viewer' && <button onClick={() => { setView('data'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'data' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
              <HardDrive size={18} />
              <span>Data Management</span>
            </button>}
            {user?.role !== 'viewer' && <button onClick={() => { onBackToHub(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium text-stone-300 hover:bg-stone-700">
              <Database size={18} />
              <span>Databases Hub</span>
            </button>}
          </div>
        </nav>
      </aside>

      <main className={`flex-1 overflow-auto ${BRAND.light} relative p-5 md:p-8`}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 border-b border-stone-300 pb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Executive Bills</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  fetchDashboard();
                  fetchFilters();
                  if (view === 'bills') fetchRows();
                }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-stone-300 bg-white text-stone-700 hover:bg-stone-100 text-sm"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {importMsg ? (
            <div className={`mb-4 p-3 rounded-sm text-sm ${importMsg.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
              {importMsg.text}
            </div>
          ) : null}

          {view === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <StatCard label="Total Bills" value={dashboard?.summary?.totalRows || 0} />
                <StatCard label="Legislatures" value={dashboard?.summary?.distinctLegislatures || 0} />
                <StatCard label="Governments" value={dashboard?.summary?.distinctGovernments || 0} />
                <StatCard label="Years" value={`${dashboard?.summary?.minYear || '—'} → ${dashboard?.summary?.maxYear || '—'}`} />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <ChartCard title="Bills by Legislature">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={legislatureChartRows} margin={{ top: 10, right: 16, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis dataKey="label" angle={-30} textAnchor="end" interval={0} height={65} tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#74c476" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Top Bill Types">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={typeLawChartRows} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={110} innerRadius={45} labelLine={false}>
                        {typeLawChartRows.map((entry, index) => (
                          <Cell key={`type-law-${entry.label}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <ChartCard title="Top Results">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={resultChartRows} margin={{ top: 10, right: 16, left: 0, bottom: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={90} tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#4E647D" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="CAP Topics">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={capTopicChartRows} margin={{ top: 10, right: 16, left: 0, bottom: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={90} tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#D76559" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          )}

          {view === 'bills' && (
            <div className="space-y-4">
              <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
                  <label className="block lg:col-span-2">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Search</span>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title, type, result..." className="w-full border border-stone-300 pl-9 pr-3 py-2 text-sm" />
                    </div>
                  </label>

                  <label className="block">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Legislature</span>
                    <select value={legislature} onChange={(e) => setLegislature(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.legislatures.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>

                  <label className="block">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Year</span>
                    <select value={year} onChange={(e) => setYear(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.years.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>

                  <label className="block">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Type</span>
                    <select value={typeLaw} onChange={(e) => setTypeLaw(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.typeLaws.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>

                  <label className="block">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Result</span>
                    <select value={result} onChange={(e) => setResult(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.results.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>

                  <label className="block lg:col-span-2">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">CAP Topic</span>
                    <select value={capTopic} onChange={(e) => setCapTopic(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.capTopics.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>

                  <label className="block lg:col-span-2">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">CAP Subtopic</span>
                    <select value={capSubtopic} onChange={(e) => setCapSubtopic(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.capSubtopics.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                </div>

                <div className="mt-3 flex gap-2">
                  <button onClick={fetchRows} className="px-3 py-2 bg-[#74c476] text-white text-sm rounded-sm hover:bg-[#5aa85c]">Apply filters</button>
                  <button
                    onClick={() => {
                      setSearch('');
                      setLegislature('');
                      setYear('');
                      setTypeLaw('');
                      setResult('');
                      setCapTopic('');
                      setCapSubtopic('');
                      setTimeout(fetchRows, 0);
                    }}
                    className="px-3 py-2 border border-stone-300 text-sm rounded-sm bg-white hover:bg-stone-100"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-200 text-sm text-stone-600">Rows: {rows.length}</div>
                <div className="overflow-auto max-h-[60vh]">
                  <table className="min-w-full text-sm">
                    <thead className="bg-stone-100 text-stone-700 uppercase text-xs tracking-wider">
                      <tr>
                        <th className="px-3 py-2 text-left">Leg.</th>
                        <th className="px-3 py-2 text-left">Year</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Result</th>
                        <th className="px-3 py-2 text-left">Government</th>
                        <th className="px-3 py-2 text-left">Party</th>
                        <th className="px-3 py-2 text-left">CAP Topic</th>
                        <th className="px-3 py-2 text-left">Title</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {rows.map((row) => (
                        <tr key={row.id} className="hover:bg-stone-50">
                          <td className="px-3 py-2 whitespace-nowrap">{row.legislature || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.year || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.typeLaw || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.result || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.typeGovernmentName || row.typeGovernment || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.politicalParty || '—'}</td>
                          <td className="px-3 py-2 min-w-[220px]">
                            <div className="font-medium text-stone-800">{row.capTopic || '—'}</div>
                            <div className="text-xs text-stone-500 mt-0.5">{row.capSubtopic || '—'}</div>
                          </td>
                          <td className="px-3 py-2 min-w-[420px]">{row.title || '—'}</td>
                        </tr>
                      ))}
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-6 text-center text-stone-500">No rows found for selected filters.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {view === 'data' && user?.role !== 'viewer' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {canImport && (
                <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-stone-100 text-stone-800 rounded-full">
                      <Upload size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-stone-800">Import Workbook</h3>
                      <p className="text-sm text-stone-500">Upload Executive_Bills_1977_2025.xls or .xlsx.</p>
                    </div>
                  </div>

                  <div className="relative border-2 border-dashed border-stone-300 rounded-sm p-10 text-center hover:border-green-600 transition-colors">
                    <input type="file" accept=".xls,.xlsx" onChange={handleImport} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    <div className="text-stone-400 flex flex-col items-center">
                      {loading ? <RefreshCw className="animate-spin mb-2" /> : <Upload className="mb-2" size={32} />}
                      <span className="font-medium text-stone-600">Click to upload XLS/XLSX file</span>
                      <span className="text-xs mt-1">Expected main sheet: dades_1977_2025</span>
                    </div>
                  </div>
                </div>
              )}

              <div className={`bg-white p-8 rounded-sm shadow-sm border border-stone-200 ${canImport ? '' : 'lg:col-span-2'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-stone-100 text-stone-800 rounded-full">
                    <Database size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-800">Export / Source</h3>
                    <p className="text-sm text-stone-500">Download data in CSV, Excel or original database format.</p>
                  </div>
                </div>

                <div className="space-y-4 text-sm text-stone-700">
                  <button onClick={handleExportCsv} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="text-green-600" size={20} />
                      <div className="text-left">
                        <div className="font-bold text-stone-800 group-hover:text-green-800">Export full CSV</div>
                        <div className="text-xs text-stone-500">All executive bills in normalized rows</div>
                      </div>
                    </div>
                    <Download size={18} />
                  </button>

                  <button onClick={handleExportXlsx} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="text-green-600" size={20} />
                      <div className="text-left">
                        <div className="font-bold text-stone-800 group-hover:text-green-800">Export full Excel (.xlsx)</div>
                        <div className="text-xs text-stone-500">Rows from executive bills database</div>
                      </div>
                    </div>
                    <Download size={18} />
                  </button>

                  <button onClick={handleExportOriginal} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                    <div className="flex items-center gap-3">
                      <HardDrive className="text-green-600" size={20} />
                      <div className="text-left">
                        <div className="font-bold text-stone-800 group-hover:text-green-800">Download full DB in original format</div>
                        <div className="text-xs text-stone-500">Current storage engine snapshot (.db SQLite / .sql PostgreSQL)</div>
                      </div>
                    </div>
                    <Download size={18} />
                  </button>

                  <div>
                    <div className="text-xs font-bold uppercase text-stone-400 mb-1">Storage engine</div>
                    <div className="capitalize">{dashboard?.storageEngine || 'sqlite'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {loading ? <div className="fixed bottom-4 right-4 bg-stone-800 text-white text-xs px-3 py-2 rounded-sm shadow-lg">Loading...</div> : null}
      </main>
    </div>
  );
}
